<template>
  <!--
    O PEIXE NA LINHA, em papel (D0, D15). A versão antiga tinha 40 estrelas, 100 confetes, chuva
    de cifrões e um dragão de partículas por raridade — o oposto do jogo calmo que o D0 pede. O
    payoff aqui é o PEIXE: arte grande no papel, nome, medida, e a raridade como cor do fio — a
    celebração continua nos sons, que já contam a história.
  -->
  <div v-if="fish" class="captura" role="dialog" :aria-label="$t('fishing.results.success')">
    <div class="captura__papel subir-surgindo" :style="{ '--fio': corDaRaridade }">
      <p class="captura__titulo">{{ $t('fishing.results.success') }}</p>

      <img class="captura__arte" :src="getFishImage(fish.name)" :alt="$t(`fishes.${fish.name}`)" />

      <p class="captura__nome">{{ $t(`fishes.${fish.name}`) }}</p>

      <p v-if="fish.critical" class="captura__critico">
        <FishIcon nome="atributo-critico" tamanho="pequeno" />
        {{ $t('fishing.critical') }}
      </p>

      <ul class="captura__medidas">
        <li class="t-numero">{{ $t('fishing.weight', { weight: fish.weight }) }}</li>
        <li>
          {{ $t('fishing.rarity') }}:
          <strong :style="{ color: corDaRaridade }">{{ $t(`fishes.${fish.rarity}`) }}</strong>
        </li>
      </ul>

      <FishButton variante="primario" bloco som="recompensa" @click="$emit('close')">
        {{ $t('fishing.continue') }}
      </FishButton>
    </div>
  </div>
</template>

<script>
import { getFishImage } from '@/shared/utils/fishesImages';
import { FishButton, FishIcon } from '@/shared/ui';

/** A cor da raridade vem dos tokens — é o MESMO aro dos encaixes, na borda do papel. */
const COR_DA_RARIDADE = {
  common: 'var(--raridade-comum)',
  uncommon: 'var(--raridade-incomum)',
  rare: 'var(--raridade-rara)',
  epic: 'var(--raridade-epica)',
  legendary: 'var(--raridade-lendaria)',
  mythic: 'var(--raridade-mitica)',
};

export default {
  name: 'CatchResult',
  components: { FishButton, FishIcon },
  props: {
    fish: {
      type: Object,
      default: null
    },
    result: {
      type: Object,
      default: null
    }
  },
  emits: ['close'],
  computed: {
    corDaRaridade() {
      return COR_DA_RARIDADE[this.fish?.rarity] || COR_DA_RARIDADE.common;
    },
  },
  methods: {
    getFishImage(name) {
      return getFishImage(name);
    }
  }
}
</script>

<style scoped>
.captura {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--e-3);
  background: rgba(10, 20, 28, 0.6);
}

.captura__papel {
  width: min(360px, 100%);
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-panel.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background: var(--papel);
  background-clip: padding-box;
  image-rendering: pixelated;
  padding: var(--e-4);
  text-align: center;
  color: var(--tinta);
  position: relative;
  box-shadow: 0 8px 0 rgba(0, 0, 0, 0.4);
}

/* O fio da raridade é um ::after, não border-bottom nem inset: no desktop o papel vira pergaminho
 * por border-image com `fill`, que pinta o miolo POR CIMA de borda avulsa e de sombra interna —
 * só um pseudo-elemento (camada de filho) fica visível nas duas peles. */
.captura__papel::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 6px;
  background: var(--fio, var(--raridade-comum));
}

/* No desktop o resultado é o PERGAMINHO, como todo modal (pedido do autor, 03/09). */
@media (min-width: 600px) {
  .captura__papel {
    width: min(440px, 100%);
    border-style: solid;
    border-color: transparent;
    border-width: 12px 24px;
    border-image: url('/ui/pergaminho.png') 12 24 fill / 12px 24px round;
    background: none;
    box-shadow: none;
  }

  /* As abas enroladas do pergaminho passam da folha: o fio encolhe para ficar SÓ no papel. */
  .captura__papel::after {
    left: var(--e-2);
    right: var(--e-2);
  }
}

.captura__titulo {
  margin: 0 0 var(--e-2);
  font-family: var(--fonte-display);
  font-size: var(--f-3);
  color: var(--tinta);
}

.captura__arte {
  display: block;
  width: 160px;
  height: 120px;
  margin: 0 auto;
  object-fit: contain;
  image-rendering: auto;
}

@media (min-width: 600px) {
  .captura__arte {
    width: 220px;
    height: 160px;
  }
}

.captura__nome {
  margin: var(--e-2) 0 0;
  font-family: var(--fonte-display);
  font-size: var(--f-3);
}

.captura__critico {
  margin: var(--e-1) 0 0;
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
  color: var(--latao);
  font-family: var(--fonte-display);
  font-size: var(--f-1);
}

.captura__medidas {
  list-style: none;
  margin: var(--e-2) 0 var(--e-3);
  padding: var(--e-2) 0 0;
  border-top: 2px dashed var(--papel-sombra);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

.captura__medidas strong {
  font-weight: 700;
}
</style>
